<template>
    <!--
        THE SHELL IS THREE SURFACES, NOT ONE.

        The frame (sidebar + topbar) paints `--ui-bg-chrome`, one surface across both halves.
        The page BEHIND the frame paints nothing at all, so the body's `--ui-bg-canvas` shows
        through — the canvas is a property of the theme, not of this layout, and
        `theme/theme.css` is where it is painted. That is what lets the house
        theme put the chrome and the panels on the SAME level and the page a level below them
        — white on neutral-100 in light, the panel mix on neutral-900 in dark — without this
        file knowing a colour. A theme that leaves both variables at `--ui-bg` renders the flat
        single-surface shell instead, hairlines only.

        THE CONTENT COLUMN HAS TWO RHYTHMS, one per form factor: `p-3 gap-3` below 1024px and
        `p-6 gap-6` from there up. The desktop value is unchanged; what changed is that a phone
        no longer spends 24px of a 360px-wide screen on the gutter either side of a card that
        already has padding of its own. `lg:` IS the shell's mobile breakpoint — Tailwind's
        `lg` is 1024px and so is `stores/app.ts`'s `isMobile` — so the gutter and the drawer
        flip at the same width. Keep them in sync.
    -->
    <div class="relative flex h-dvh overflow-hidden">
        <!--
            THE MOBILE BACKDROP. It paints the same black the theme gives every dialog
            (`theme/theme.config.ts` — modal, slideover, drawer), because a drawer is one of them
            even though it is our own component: one dimming, whatever is over the page.

            It is `fixed inset-0`, not absolute, so it covers the visible viewport rather than
            whatever box an ancestor happens to be — and it sits UNDER the drawer (z-40 to the
            drawer's z-50) and OVER the topbar, which is what makes tapping beside the menu the
            obvious way out of it.

            It FADES, and the fade is the same shape as the drawer's slide: 200ms out on the
            way in, 150ms in on the way out. Both ends of the transition are named as classes
            rather than left to the element's base state — `opacity-0` → `opacity-100` — so the
            browser has two values to interpolate between instead of one value and a default.
        -->
        <Transition
            enter-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isMobile && menuOpen"
                class="fixed inset-0 z-40 bg-black/50 dark:bg-black/70"
                aria-hidden="true"
                @click="closeMenu"
            />
        </Transition>

        <!--
            ONE SIDEBAR COMPONENT, TWO ROLES — and the roles are rendered SEPARATELY, because
            only one of them may animate. Below 1024px it is an overlay that slides in from the
            left; at or above it, it is the layout's own left column and must simply be there,
            with no slide when the window crosses the breakpoint. Two mutually exclusive `v-if`s
            is what buys that: the desktop column is outside the `<Transition>` entirely.

            The drawer is `fixed inset-y-0 left-0` — the visible viewport, not whatever box an
            ancestor happens to be — `h-dvh` (100vh is taller than what a mobile browser shows
            while its URL bar is out, which is exactly how the user row at its bottom went
            off-screen) and z-50, above the backdrop.

            `motion-reduce:transition-none` on both halves: a reader who has asked the system
            for less motion gets the drawer, not the slide.
        -->
        <Transition
            enter-active-class="transition-transform duration-200 ease-out motion-reduce:transition-none"
            enter-from-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-150 ease-in motion-reduce:transition-none"
            leave-from-class="translate-x-0"
            leave-to-class="-translate-x-full"
        >
            <Sidebar v-if="isMobile && menuOpen" class="fixed inset-y-0 left-0 z-50 h-dvh" />
        </Transition>

        <Sidebar v-if="!isMobile" />

        <div class="relative flex flex-col flex-1 min-w-0">
            <Topbar />

            <!--
                `[&>*]:shrink-0` IS WHAT MAKES THE PAGE SCROLL. `main` is the scroll port and a
                flex column, so every page root is a flex ITEM of it. Flexbox only protects an
                item from being shrunk below its content when its overflow is `visible`;
                @nuxt/ui's UCard root (AppCard) is `overflow-hidden`, and UTable's root clips
                too — so without this the card is squeezed to the viewport, clips its own
                content, and `main` never overflows: nothing scrolls, the pagination is out of
                reach. Stated once here rather than as a `shrink-0` every page would have to
                remember. A page that WANTS an internal scroll port (a full-height map, a
                board) opts in explicitly with `h-full min-h-0` on its own root.
            -->
            <main class="flex flex-col gap-3 flex-1 overflow-y-auto p-3 lg:gap-6 lg:p-6 [&>*]:shrink-0">
                <router-view />
            </main>
        </div>

        <!-- The Claude sign-in dialog is global: any page can raise it (a card, the workspace
             page, the settings list), so it is mounted once by the shell rather than by each
             of them. It renders nothing until it is opened. -->
        <ClaudeAccountDialog />

        <!-- Same story for the instance-type picker: a start is written in libs/environment.ts,
             which has no template, so the one dialog is mounted here and opened through a
             promise. A user allowed a single type never opens it. -->
        <InstanceTypeDialog />
    </div>
</template>

<script lang="ts" setup>
    import ClaudeAccountDialog from '@/components/ClaudeAccountDialog.vue'
    import InstanceTypeDialog from '@/components/InstanceTypeDialog.vue'
    import { useImageGate } from '@/libs/image'
    import { useLayout, useLayoutShell } from '@/layouts/layout'
    import Sidebar from '@/layouts/components/Sidebar.vue'
    import Topbar  from '@/layouts/components/Topbar.vue'

    /**
     * The shell is where the drawer's behaviour is wired — Escape, route changes, the
     * breakpoint and the body-scroll lock — and it is wired ONCE, here, because there is one
     * shell per window. `layout.ts` owns what each of those does.
     */
    const { isMobile, menuOpen, closeMenu } = useLayout()

    useLayoutShell()

    /**
     * THE BOOT GATE, watched for as long as the shell is up. The router checks it on every
     * navigation; this is what catches a build that STARTS under a tab already open — an
     * admin pressing Build new, the gate firing after an upgrade — and hands the app over to
     * the preparing page without waiting for the next click (invariant 27). It is one small
     * `GET /image` every thirty seconds and it holds the app's only poll of it.
     */
    useImageGate()
</script>
